import { useCallback, useEffect, useState } from "react";
import { Search, Users, Download, ChevronDown, ChevronUp } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import NotesPanel from "../components/NotesPanel";
import { adminApi } from "../lib/adminApi";

const STATUSES = ["New", "Reviewing", "Shortlisted", "Rejected", "Hired"];

const STATUS_BADGE = {
  New: "bg-brand-red text-white",
  Reviewing: "bg-brand-surface text-brand-graphite border border-border",
  Shortlisted: "bg-brand-graphite text-white",
  Rejected: "bg-neutral-100 text-neutral-500",
  Hired: "bg-green-700 text-white",
};

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [jobId, setJobId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/jobs").then((res) => setJobs(res.data.items)).catch(() => {});
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/applications", { params: { search, status, jobId } });
      setItems(res.data.items);
      setCounts(res.data.counts);
    } catch (err) {
      // AdminLayout handles the redirect on auth failure
    } finally {
      setLoading(false);
    }
  }, [search, status, jobId]);

  useEffect(() => {
    const t = setTimeout(fetchApplications, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchApplications, search]);

  const updateStatus = async (a, newStatus) => {
    const res = await adminApi.patch(`/admin/applications/${a.id}/status`, { status: newStatus });
    setItems((prev) => prev.map((it) => (it.id === a.id ? res.data : it)));
    setCounts((c) => ({ ...c, [a.status]: (c[a.status] || 1) - 1, [newStatus]: (c[newStatus] || 0) + 1 }));
  };

  const downloadResume = async (a) => {
    const res = await adminApi.get(`/admin/applications/${a.id}/resume`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = a.resumeFilename || `resume_${a.id}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Job Applications">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {[["Total", counts.total, "admin-app-count-total"], ...STATUSES.map((s) => [s, counts[s] || 0, `admin-app-count-${s.toLowerCase()}`])].map(([label, value, testid]) => (
          <div key={label} className="border border-border bg-white p-3 sm:p-4" data-testid={testid}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-grey">{label}</p>
            <p className="mt-0.5 text-2xl font-semibold text-brand-graphite">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-grey" aria-hidden="true" />
          <input
            type="search"
            className="field-input pl-10"
            placeholder="Search by name, email, role or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-app-search-input"
          />
        </div>
        <select className="field-input sm:w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-app-status-filter">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className="field-input sm:w-56" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="admin-app-job-filter">
          <option value="all">All openings</option>
          {jobs.map((j) => (
            <option key={j.id} value={j.id}>{j.title}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-applications-list">
        {loading ? (
          <p className="py-12 text-center text-sm text-brand-grey">Loading applications…</p>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-12 text-center" data-testid="admin-applications-empty">
            <Users className="mx-auto h-8 w-8 text-brand-grey" aria-hidden="true" />
            <p className="mt-3 text-sm text-brand-grey">No applications match your filters.</p>
          </div>
        ) : (
          items.map((a) => (
            <div key={a.id} className="border border-border bg-white" data-testid={`admin-app-${a.id}`}>
              <div className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-brand-graphite">{a.fullName}</p>
                    <span className="bg-brand-surface px-2 py-0.5 text-xs font-medium text-brand-grey">{a.jobTitle}</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[a.status]}`} data-testid={`admin-app-status-${a.id}`}>
                      {a.status}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-brand-grey">
                    {a.email} · {a.phone} · {a.currentLocation} · {a.totalExperience} total / {a.relevantExperience} relevant
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-400">
                    {new Date(a.createdAt).toLocaleString()} · notice: {a.noticePeriod}
                    {a.currentCompany ? ` · ${a.currentCompany}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="field-input w-36 px-2 py-2 text-xs"
                    value={a.status}
                    onChange={(e) => updateStatus(a, e.target.value)}
                    data-testid={`admin-app-status-select-${a.id}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button type="button" className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-xs" onClick={() => downloadResume(a)} data-testid={`admin-app-resume-${a.id}`}>
                    <Download className="h-3.5 w-3.5" aria-hidden="true" /> Résumé
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                    className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-xs"
                    data-testid={`admin-app-toggle-details-${a.id}`}
                  >
                    Details {expanded === a.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              {expanded === a.id && (
                <div className="space-y-3 border-t border-border bg-brand-surface p-4 sm:p-5" data-testid={`admin-app-details-${a.id}`}>
                  <div className="grid gap-2 text-sm text-brand-graphite sm:grid-cols-2">
                    <p><span className="font-semibold">LinkedIn:</span> {a.linkedinUrl ? <a href={a.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-brand-red underline underline-offset-2 break-all">{a.linkedinUrl}</a> : "—"}</p>
                    <p><span className="font-semibold">Portfolio / GitHub:</span> {a.portfolioUrl ? <a href={a.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-brand-red underline underline-offset-2 break-all">{a.portfolioUrl}</a> : "—"}</p>
                    <p><span className="font-semibold">Résumé file:</span> {a.resumeFilename} ({Math.max(1, Math.round(a.resumeSize / 1024))} KB)</p>
                  </div>
                  {a.coverMessage && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Cover Message</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-brand-graphite">{a.coverMessage}</p>
                    </div>
                  )}
                  <NotesPanel parentType="application" parentId={a.id} />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
